import { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts/core'
import type { ChartPin, Series } from '../api/types'
import { SNAP_PX, pinOverlayPoints, snapToStored, stretchOf } from '../lib/pins'
import { formatDateTime } from '../lib/time'
import { pinsForChart } from '../lib/chartPins'
import { ChartPinMarker } from './ChartPinUI'

/** A movement shorter than this, in chart pixels, is a click, not a drag. */
const CLICK_PX = 6

export interface PinTarget { parameter: string; ts: string; ts_end?: string }

interface Placed { pin: ChartPin; x: number; y: number; width: number; height: number }

interface Props {
  container: React.RefObject<HTMLDivElement>
  /** True once the ECharts instance exists (TimeSeriesChart). */
  ready: boolean
  series: Series[]
  /** The lines on show (not hidden in the legend): only these can be
   *  pinned, and only their pins' boxes are drawn. The same objects as in
   *  `series`. */
  shown: Series[]
  pins: ChartPin[]
  pinMode: boolean
  /** The line name and colours of a series, as the chart draws it. */
  look: (parameter: string) => { name: string; colour: string; text: string; unit: string } | null
  canRemove: (pin: ChartPin) => boolean
  onPin: (target: PinTarget) => void
  onMiss: (message: string) => void
  onRemove: (pin: ChartPin) => void | Promise<void>
}

/** The pins' note boxes over a chart, and Pin mode's pointer handling.
 *
 *  Positions come from ECharts itself (`convertToPixel`, and `offsetX` /
 *  `offsetY` on its own zr events) inside the ChartHost frame, never from
 *  `clientX` less a bounding box: the page is drawn under CSS zoom, and the
 *  frame is what cancels it (HANDBOOK, "UI zoom"). */
export function PinLayer({ container, ready, series, shown, pins, pinMode, look, canRemove,
                           onPin, onMiss, onRemove }: Props) {
  const [placed, setPlaced] = useState<Placed[]>([])
  const [band, setBand] = useState<[number, number] | null>(null)
  const latest = useRef({ series, shown, pins, onPin, onMiss })
  latest.current = { series, shown, pins, onPin, onMiss }

  // Where each pin's box sits: re-measured after every render of the chart
  // (zoom, resize, a new option), kept only when something moved, so a
  // tooltip's re-render does not re-render the page.
  useEffect(() => {
    const chart = instance(container)
    if (!chart) return
    let last = ''
    const place = () => {
      const next = pinsForChart(pins, series).flatMap((pin) => {
        const anchor = anchorOf(series, pin)
        if (!anchor) return []
        const index = series.findIndex((s) => s.parameter === pin.parameter)
        if (!shown.includes(series[index]!)) return []
        const pixel = chart.convertToPixel({ seriesIndex: index }, anchor) as number[] | undefined
        if (!pixel || !chart.containPixel({ gridIndex: 0 }, pixel)) return []
        const width = chart.getWidth() || 1
        return [{ pin, x: pixel[0]!, y: pixel[1]!, width, height:chart.getHeight() }]
      })
      const key = JSON.stringify(next.map((p) => [p.pin.id, Math.round(p.x), Math.round(p.y), p.width, p.height]))
      if (key !== last) {
        last = key
        setPlaced(next)
      }
    }
    place()
    chart.on('finished', place)
    return () => { chart.off('finished', place) }
  }, [container, ready, series, shown, pins])

  // Pin mode: a click pins the nearest stored reading, a drag the stretch
  // between its ends on the line the drag started on.
  useEffect(() => {
    const chart = instance(container)
    if (!chart || !pinMode) return
    const zr = chart.getZr()
    let start: { x: number; y: number } | null = null
    const time = (x: number) =>
      (chart.convertFromPixel({ gridIndex: 0 }, [x, 0]) as number[])[0]!
    const xPx = (t: number) =>
      (chart.convertToPixel({ gridIndex: 0 }, [t, 0]) as number[])[0]!
    const yPx = (s: Series, v: number) => {
      const index = latest.current.series.indexOf(s)
      return (chart.convertToPixel({ seriesIndex: index }, [0, v]) as number[])[1]!
    }
    // A press outside the plot, or the pointer leaving the chart, ends any
    // drag in progress: a start left behind would let a later release pin a
    // stretch nobody drew.
    const down = (e: { offsetX: number; offsetY: number }) => {
      start = chart.containPixel({ gridIndex: 0 }, [e.offsetX, e.offsetY])
        ? { x: e.offsetX, y: e.offsetY } : null
    }
    const out = () => {
      start = null
      setBand(null)
    }
    const move = (e: { offsetX: number }) => {
      if (start && Math.abs(e.offsetX - start.x) >= CLICK_PX) setBand([start.x, e.offsetX])
    }
    const up = (e: { offsetX: number; offsetY: number }) => {
      if (!start) return
      const from = start
      start = null
      setBand(null)
      const { shown: lines, onPin: pin, onMiss: miss } = latest.current
      const hit = snapToStored(lines, time(from.x), yPx, from.y, SNAP_PX, xPx)
      if (!hit) {
        miss('No stored reading there to pin. Click on a line, or drag along it.')
        return
      }
      if (Math.abs(e.offsetX - from.x) < CLICK_PX) {
        pin({ parameter: hit.parameter, ts: hit.ts })
        return
      }
      const line = lines.find((s) => s.parameter === hit.parameter)!
      const stretch = stretchOf(line, time(from.x), time(e.offsetX))
      if (!stretch) {
        miss('A stretch needs two stored readings inside the drag.')
        return
      }
      pin({ parameter: hit.parameter, ts: stretch.ts, ts_end: stretch.ts_end })
    }
    zr.on('mousedown', down)
    zr.on('mousemove', move)
    zr.on('mouseup', up)
    zr.on('globalout', out)
    return () => {
      zr.off('mousedown', down)
      zr.off('mousemove', move)
      zr.off('mouseup', up)
      zr.off('globalout', out)
      setBand(null)
    }
  }, [container, ready, pinMode])

  return (
    <>
      {band && (
        <div className="pin-drag-band" aria-hidden="true"
             style={{ left: Math.min(...band), width: Math.abs(band[1] - band[0]) }} />
      )}
      {placed.map(({ pin, x, y, width, height }) => {
        const seen = look(pin.parameter)
        if (!seen) return null
        return (
          <ChartPinMarker key={pin.id} x={x} y={y} width={width} height={height} color={seen.colour} number={pinsForChart(pins, series).findIndex(p => p.id === pin.id) + 1}
            reading={{ label:seen.name, value:pin.ts_end === null ? valueText(pin.value, seen.unit) : `${valueText(pin.value, seen.unit)} → ${valueText(pin.value_end, seen.unit)}`,
              time:`${formatDateTime(new Date(pin.ts))}${pin.ts_end ? ` – ${formatDateTime(new Date(pin.ts_end))}` : ''} · UTC+4`,
              note:pin.note, author:pin.created_by_name || 'Not recorded', createdAt:`${formatDateTime(pin.created_at)} · UTC+4` }}
            onRemove={canRemove(pin) ? () => onRemove(pin) : undefined} />
        )
      })}
    </>
  )
}

function instance(container: React.RefObject<HTMLDivElement>) {
  return container.current ? echarts.getInstanceByDom(container.current) : undefined
}

/** Where a pin's box hangs: its own reading, or for a stretch the middle
 *  stored reading inside it. */
function anchorOf(series: Series[], pin: ChartPin): [number, number] | null {
  if (pin.ts_end === null) {
    return pin.value === null ? null : [Date.parse(pin.ts), pin.value]
  }
  const line = series.find((s) => s.parameter === pin.parameter)
  if (!line) return null
  const stored = pinOverlayPoints(line, pin).filter((p) => p[1] !== null)
  const middle = stored[Math.floor(stored.length / 2)]
  return middle ? [Date.parse(middle[0]), middle[1]!] : null
}

function valueText(value: number | null, unit: string): string {
  // The stored value, unrounded, as the tooltip shows it.
  return value === null ? '—' : `${value}${unit ? ` ${unit}` : ''}`
}
